import { inject, Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { ConfigFacade } from '@rx-applications/clientsite/shared/config/data-access';
import { ResultsFacade } from '@rx-applications/clientsite/search/location/api';
import { SearchFacade } from '@rx-applications/clientsite/search/searchbox/api';

import { FeatureFlagFacade } from '@rx-applications/shared/clientsite/feature-flags';
import { AmenityName, FreeCancellationFilter, MealOption, MealOptionsName } from '@rx-applications/clientsite/shared/utils';
import {
  FilterFacade,
  mealOptionFilters,
  otherFilters,
  parkingFilters,
} from '@rx-applications/clientsite/search/filter/data-access';
import {
  Filter,
  FilterCriteria,
  FilterGroup,
  FilterTypeName,
  hasRoomexExclusiveRate,
  loyaltyFilters,
  PropertyTypeState,
  rateFilters,
} from '@rx-applications/clientsite/search/shared';

@Injectable({ providedIn: 'root' })
export class FilterService {
  #featureFlagFacade = inject(FeatureFlagFacade);

  constructor(
    public resultsFacade: ResultsFacade,
    public filterFacade: FilterFacade,
    public configFacade: ConfigFacade,
    public searchFacade: SearchFacade,
  ) {}

  get popularFilterList$(): Observable<(Filter | FilterGroup)[]> {
    return combineLatest([this.configFacade.hasPolicyEnabled$, this.filterFacade.filterCriteria$]).pipe(
      map(([hasPolicy, criteria]) => {
        const filtersList: (Filter | FilterGroup)[] = [];
        if (hasPolicy) {
          filtersList.push(new Filter('inPolicy'));
        }

        filtersList.concat(mealOptionFilters);
        return this.markForCheck(filtersList, criteria);
      }),
    );
  }

  get popularFilterListCombined$(): Observable<(Filter | FilterGroup)[]> {
    return combineLatest([
      this.freeCancellationFilter$,
      this.mealOptionFilters$,
      this.loyaltyProgramFilter$,
      this.popularFilterList$,
    ]).pipe(
      map(([freeCancellation, mealOptions, loyaltyProgram, popular]) => [
        ...(freeCancellation ? [freeCancellation] : []),
        ...(mealOptions.length ? mealOptions : []),
        ...(loyaltyProgram ? [loyaltyProgram] : []),
        ...(popular.length ? popular : []),
      ]),
    );
  }

  get mealOptionFilters$(): Observable<Filter[]> {
    return combineLatest([this.filterFacade.mealOptionsCriteria$, this.searchFacade.mealOptionFilters$]).pipe(
      map(([storeCriteria, queryCriteria]) => {
        return storeCriteria.length
          ? mealOptionFilters.map((mealOption) => {
              mealOption.checked = storeCriteria.some((mealOptionName) => mealOptionName === mealOption.name);
              return mealOption;
            })
          : queryCriteria;
      }),
    );
  }

  get parkingFilterList$(): Observable<(Filter | FilterGroup)[]> {
    return combineLatest([
      of(parkingFilters),
      this.filterFacade.filterCriteria$,
      this.configFacade.isShowEvParkingFilterEnabled$,
      this.#featureFlagFacade.showTruckParkingFilter$,
    ]).pipe(
      map(([filtersOrFilterGroups, criteria, isShowEvParkingFilterEnabled, showTruckParkingFilter]) => {
        let filtersList = filtersOrFilterGroups;

        if (!isShowEvParkingFilterEnabled) {
          filtersList = filtersList.filter((filter) => filter.name !== 'evparking');
        }
        if (!showTruckParkingFilter) {
          filtersList = filtersList.filter((filter) => filter.name !== 'truckparking' && filter.name !== 'freetruckparking');
        }

        return this.markForCheck([...filtersList], criteria);
      }),
    );
  }

  get otherFilterList$(): Observable<(Filter | FilterGroup)[]> {
    return combineLatest([this.resultsFacade.selectResults$, this.filterFacade.filterCriteria$]).pipe(
      map(([results, criteria]) => {
        const filtersList = [...otherFilters];
        const mergedResults = [...results.recommended, ...results.other, ...results.nearby];
        if (mergedResults.some(hasRoomexExclusiveRate)) {
          filtersList.push(new Filter('exclusiveRate'));
        }
        return this.markForCheck(filtersList, criteria);
      }),
    );
  }

  get freeCancellationFilter$(): Observable<Filter> {
    return combineLatest([rateFilters, this.filterFacade.freeCancellationCriteria$]).pipe(
      map(([filter, value]: [Filter, FreeCancellationFilter]) => {
        filter.checked = value === FreeCancellationFilter.FreeCancellation;
        return filter;
      }),
    );
  }

  get loyaltyProgramFilter$() {
    return combineLatest([
      loyaltyFilters,
      this.configFacade.loyaltyEnabled$,
      this.#featureFlagFacade.showLoyaltyGuaranteed$,
      this.filterFacade.loyaltyGuaranteedCriteria$,
    ]).pipe(
      map(([filter, loyaltyEnabled, showLoyaltyGuaranteed, loyaltyGuaranteed]: [Filter, boolean, boolean, boolean]) => {
        filter.checked = loyaltyGuaranteed;
        if (loyaltyEnabled && showLoyaltyGuaranteed) {
          return filter;
        }
        return null;
      }),
    );
  }

  get combinedPropertyTypeFilterList$(): Observable<Filter[]> {
    return combineLatest([this.filterFacade.propertyTypeFilter$, this.filterFacade.additionalPropertyTypeFilter$]).pipe(
      map(([propertyTypeFilters, additionalPropertyTypeFilters]) => [
        ...this.getPropertyConvertedToFilter(propertyTypeFilters),
        ...this.getPropertyConvertedToFilter(additionalPropertyTypeFilters),
      ]),
    );
  }

  private getPropertyConvertedToFilter(propertyTypeFilters: PropertyTypeState[]): Filter[] {
    return propertyTypeFilters.map((propertyTypeState) => {
      const filter = new Filter(propertyTypeState.label as FilterTypeName);
      filter.name = propertyTypeState.name as FilterTypeName;
      filter.label = propertyTypeState.label;
      filter.checked = propertyTypeState.checked;
      return filter;
    });
  }

  trackMealOption = (_: number, { label }: Filter) => label;

  getMealOption(filtersState: Filter[], checked: boolean, value: string): MealOption {
    const isFilterChecked = (mealOptionName: MealOptionsName) =>
      filtersState.find((filterState) => filterState.name === mealOptionName && filterState.value) || false;
    if ((value === MealOptionsName.MustIncludeDinner && checked) || isFilterChecked(MealOptionsName.MustIncludeDinner)) {
      return MealOption.IncludesDinner;
    }

    if (value === MealOptionsName.MustIncludeDinner && !checked && isFilterChecked(MealOptionsName.AllBreakfastOptions)) {
      return MealOption.AllBreakfastOptions;
    }

    if (value === MealOptionsName.AllBreakfastOptions && checked) {
      return MealOption.AllBreakfastOptions;
    }

    return MealOption.AnyMealOption;
  }

  convertToMealOption(value: string): MealOption {
    switch (value) {
      case MealOptionsName.AllBreakfastOptions:
        return MealOption.AllBreakfastOptions;
      case MealOptionsName.MustIncludeDinner:
        return MealOption.IncludesDinner;
      default:
        return MealOption.AnyMealOption;
    }
  }

  private markForCheck(filtersList: (Filter | FilterGroup)[], criteria: FilterCriteria) {
    filtersList.forEach((filter) => {
      if (filter instanceof FilterGroup) {
        filter.filters.forEach((fg) => (fg.checked = criteria.amenities.includes(<AmenityName>fg.name)));
      } else {
        filter.checked = criteria.amenities.includes(<AmenityName>filter.name);
      }
    });
    return filtersList;
  }
}
